import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  Volume2,
  VolumeX,
  Megaphone,
  MegaphoneOff,
  Gem,
  Target,
} from "lucide-react";
import { useVictoryMusic } from "@/hooks/useVictoryMusic";
import { GameResultsScreen } from "@/components/study/GameResultsScreen";
import { Card } from "@/types/linguadeck";
import { useGamification } from "@/contexts/GamificationContext";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";
import { playPop, playSuccess, playError } from "@/lib/studySfx";

const CRYSTALS_PER_CORRECT = 5;
const VICTORY_DELAY_MS = 1500;
const MAX_TILES = 12;
const MIN_TILES = 6;

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Floating +N crystals animation */
function FloatingCrystals({
  amount,
  onComplete,
}: {
  amount: number;
  onComplete: () => void;
}) {
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 flex items-center justify-center z-50"
      initial={false}
    >
      <motion.span
        className="inline-flex items-center gap-1.5 text-2xl font-bold text-[hsl(var(--accent2))] drop-shadow-lg whitespace-nowrap"
        initial={{ opacity: 1, y: 0, scale: 1 }}
        animate={{ opacity: 0, y: -80, scale: 1.2 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        onAnimationComplete={onComplete}
      >
        +{amount}
        <Gem className="w-6 h-6" aria-hidden />
      </motion.span>
    </motion.div>
  );
}

interface SecretMosaicModeProps {
  cards: Card[];
  lang?: string;
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
}

export default function SecretMosaicMode({
  cards,
  lang: deckLang,
  onBack,
  onProgressComplete,
}: SecretMosaicModeProps) {
  const { t } = useTranslation();
  const { addCrystals, finishStudySession } = useGamification();
  const {
    isSoundEnabled,
    isVoiceEnabled,
    gameVolume,
    voiceVolume,
    setGameVolume,
    setVoiceVolume,
    setVoiceEnabled,
    toggleSound,
    toggleVoice,
  } = useSoundStore();

  const filteredCards = useMemo(
    () =>
      cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );

  const deckKey = useMemo(
    () => filteredCards.map((c) => c.id ?? "").join(","),
    [filteredCards]
  );

  /** Tiles for this round: take up to MAX_TILES cards, each tile = one card (show translation). */
  const [roundCards, setRoundCards] = useState<Card[]>([]);
  /** Indices (into roundCards) that have been cleared. */
  const [clearedSet, setClearedSet] = useState<Set<number>>(new Set());
  /** Current target index (term to find). */
  const [targetIndex, setTargetIndex] = useState<number>(0);
  const [sessionCrystals, setSessionCrystals] = useState(0);
  const [floatingCrystals, setFloatingCrystals] = useState<number | null>(null);
  const [showResultsAfterDelay, setShowResultsAfterDelay] = useState(false);
  const [shakeIndex, setShakeIndex] = useState<number | null>(null);
  const victoryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasSpokenInitialTargetRef = useRef(false);

  useVictoryMusic({
    isGameFinished: showResultsAfterDelay,
    isSoundEnabled,
    gameVolume,
  });


  /** Initialize round: shuffle cards and take up to MAX_TILES; pick random initial target. */
  useEffect(() => {
    if (filteredCards.length === 0) return;
    hasSpokenInitialTargetRef.current = false;
    const count = Math.min(
      MAX_TILES,
      Math.max(MIN_TILES, Math.min(filteredCards.length, 12))
    );
    const shuffled = shuffleArray(filteredCards).slice(0, count);
    setRoundCards(shuffled);
    setClearedSet(new Set());
    setTargetIndex(Math.floor(Math.random() * count));
  }, [deckKey, filteredCards]);

  const remainingIndices = useMemo(
    () =>
      roundCards
        .map((_, i) => i)
        .filter((i) => !clearedSet.has(i)),
    [roundCards, clearedSet]
  );

  const currentTargetCard = roundCards[targetIndex] ?? null;
  const isTargetCleared = currentTargetCard != null && clearedSet.has(targetIndex);

  /** Pick next target randomly from remaining unrevealed tiles. */
  const advanceTarget = useCallback(() => {
    if (remainingIndices.length === 0) return;
    const randomIdx = Math.floor(Math.random() * remainingIndices.length);
    const newIdx = remainingIndices[randomIdx];
    setTargetIndex(newIdx);
    const newCard = roundCards[newIdx];
    if (newCard && isVoiceEnabled && (newCard.front || "").trim()) {
      void playAudio({
        card: newCard,
        side: "front",
        deckLang: deckLang ?? "en",
        volume: voiceVolume,
      });
    }
  }, [remainingIndices, roundCards, isVoiceEnabled, voiceVolume, deckLang]);

  const handlePlayAgain = useCallback(() => {
    hasSpokenInitialTargetRef.current = false;
    setShowResultsAfterDelay(false);
    setSessionCrystals(0);
    const count = Math.min(
      MAX_TILES,
      Math.max(MIN_TILES, Math.min(filteredCards.length, 12))
    );
    const shuffled = shuffleArray(filteredCards).slice(0, count);
    setRoundCards(shuffled);
    setClearedSet(new Set());
    setTargetIndex(Math.floor(Math.random() * count));
  }, [filteredCards]);

  const handleBackToDeck = useCallback(() => {
    onBack?.();
  }, [onBack]);

  /** When target was cleared or invalid, pick a new random target from remaining. */
  useEffect(() => {
    if (remainingIndices.length === 0) return;
    if (isTargetCleared || !remainingIndices.includes(targetIndex)) {
      const randomIdx = Math.floor(Math.random() * remainingIndices.length);
      setTargetIndex(remainingIndices[randomIdx]);
    }
  }, [remainingIndices, targetIndex, isTargetCleared]);

  useEffect(() => {
    return () => {
      if (victoryTimeoutRef.current) clearTimeout(victoryTimeoutRef.current);
    };
  }, []);

  const handleTileClick = useCallback(
    (cardIndex: number) => {
      if (clearedSet.has(cardIndex)) return;
      const card = roundCards[cardIndex];
      const targetCard = roundCards[targetIndex];
      if (!card || !targetCard) return;

      if (!hasSpokenInitialTargetRef.current && isVoiceEnabled && (targetCard.front || "").trim()) {
        hasSpokenInitialTargetRef.current = true;
        void playAudio({
          card: targetCard,
          side: "front",
          deckLang: deckLang ?? "en",
          volume: voiceVolume,
        });
      }
      if (isVoiceEnabled && (card.back || "").trim()) {
        void playAudio({
          card,
          side: "back",
          deckLang: deckLang ?? "en",
          volume: voiceVolume,
        });
      }
      if (isSoundEnabled) playPop(gameVolume * 0.6);

      const isCorrect = card.id === targetCard.id || (
        (card.front === targetCard.front && card.back === targetCard.back)
      );

      if (isCorrect) {
        if (isSoundEnabled) playSuccess(gameVolume * 0.7);
        const result = addCrystals(CRYSTALS_PER_CORRECT, t("secretMosaic.rewardReason"), {
          isLearningAction: true,
          silent: true,
        });
        const totalAdd = typeof result === "object" ? result.totalAdd : CRYSTALS_PER_CORRECT;
        setFloatingCrystals(totalAdd);
        setSessionCrystals((prev) => prev + totalAdd);
        const newCleared = new Set(clearedSet).add(cardIndex);
        setClearedSet(newCleared);
        if (roundCards.length > 0 && onProgressComplete) {
          const pct = Math.min(100, Math.round((newCleared.size / roundCards.length) * 100));
          onProgressComplete(pct);
        }
        if (newCleared.size >= roundCards.length) {
          finishStudySession(roundCards.length, false);
          victoryTimeoutRef.current = setTimeout(() => {
            setShowResultsAfterDelay(true);
            victoryTimeoutRef.current = null;
          }, VICTORY_DELAY_MS);
        } else {
          advanceTarget();
        }
      } else {
        if (isSoundEnabled) playError(gameVolume * 0.8);
        setShakeIndex(cardIndex);
        setTimeout(() => setShakeIndex(null), 500);
      }
    },
    [
      roundCards,
      targetIndex,
      clearedSet,
      advanceTarget,
      addCrystals,
      finishStudySession,
      isSoundEnabled,
      isVoiceEnabled,
      gameVolume,
      voiceVolume,
      deckLang,
      t,
      onProgressComplete,
    ]
  );

  const headerRow = (
    <header className="flex justify-between items-center w-full max-w-2xl mx-auto mb-6 gap-4 relative z-10">
      <div className="min-w-0 flex-1">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/20 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-medium transition-colors shadow-lg"
            aria-label={t("studyMenu.backToDeck")}
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            {t("studyMenu.backToDeck")}
          </button>
        ) : (
          <div />
        )}
      </div>
      <div className="grid grid-cols-[64px_auto] gap-x-2 gap-y-1 items-center shrink-0">
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={gameVolume}
          onChange={(e) => setGameVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.gameVolume")}
        />
        <button
          type="button"
          onClick={toggleSound}
          className={`p-2 rounded-lg hover:bg-white/10 transition-colors w-9 h-9 flex items-center justify-center ${isSoundEnabled ? "text-[hsl(var(--primary))]" : "text-white/50"}`}
          aria-label={isSoundEnabled ? t("common.muteMusic") : t("common.unmuteMusic")}
        >
          {isSoundEnabled ? (
            <Volume2 className="w-5 h-5" />
          ) : (
            <VolumeX className="w-5 h-5" />
          )}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={voiceVolume}
          onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.voiceVolume")}
        />
        <button
          type="button"
          onClick={toggleVoice}
          className={`p-2 rounded-lg hover:bg-white/10 transition-colors w-9 h-9 flex items-center justify-center ${isVoiceEnabled ? "text-[hsl(var(--primary))]" : "text-white/50"}`}
          aria-label={isVoiceEnabled ? t("common.voiceModeOn") : t("common.voiceModeOff")}
        >
          {isVoiceEnabled ? (
            <Megaphone className="w-5 h-5" />
          ) : (
            <MegaphoneOff className="w-5 h-5" />
          )}
        </button>
      </div>
    </header>
  );

  if (filteredCards.length === 0) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto px-4">
        {headerRow}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-8 text-center">
          <p className="text-lg font-medium text-[hsl(var(--foreground))]">
            {t("secretMosaic.needCards")}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            {t("secretMosaic.addMoreCards")}
          </p>
        </div>
      </div>
    );
  }

  if (showResultsAfterDelay) {
    return (
      <div className="relative flex flex-col flex-1 min-h-0 w-full max-w-2xl mx-auto px-4 overflow-hidden">
        {/* Keep the dark gradient mounted so header (white text) stays visible */}
        <div
          className="fixed inset-0 z-0 pointer-events-none"
          aria-hidden
        >
          <div
            className="absolute inset-0 opacity-100 secret-mosaic-bg"
            style={{
              background: `
                radial-gradient(ellipse 120% 80% at 20% 20%, rgba(88, 28, 135, 0.85) 0%, transparent 50%),
                radial-gradient(ellipse 100% 100% at 80% 80%, rgba(30, 27, 75, 0.9) 0%, transparent 50%),
                radial-gradient(ellipse 80% 120% at 50% 50%, rgba(120, 53, 15, 0.4) 0%, transparent 45%),
                radial-gradient(ellipse 90% 90% at 70% 20%, rgba(49, 46, 129, 0.6) 0%, transparent 40%),
                linear-gradient(160deg, #0f0a1e 0%, #1a0a2e 30%, #0d0d1a 60%, #0a0f1e 100%)
              `,
            }}
          />
          <div
            className="absolute inset-0 secret-mosaic-shine"
            style={{
              background: `
                radial-gradient(circle at 30% 30%, rgba(216, 180, 254, 0.12) 0%, transparent 40%),
                radial-gradient(circle at 70% 70%, rgba(251, 191, 36, 0.08) 0%, transparent 40%)
              `,
            }}
          />
        </div>
        <style>{`
          @keyframes secret-mosaic-shift {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.95; transform: scale(1.02); }
          }
          @keyframes secret-mosaic-shine-move {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
          .secret-mosaic-bg {
            animation: secret-mosaic-shift 8s ease-in-out infinite;
          }
          .secret-mosaic-shine {
            animation: secret-mosaic-shine-move 6s ease-in-out infinite;
          }
        `}</style>
        <div className="relative z-10">
          <GameResultsScreen
            sessionCrystals={sessionCrystals}
            onPlayAgain={handlePlayAgain}
            onBackToDeck={handleBackToDeck}
            headerRow={headerRow}
          />
        </div>
      </div>
    );
  }

  const displayTargetCard = remainingIndices.length > 0
    ? roundCards[remainingIndices.includes(targetIndex) ? targetIndex : remainingIndices[0]]
    : null;

  return (
    <div className="relative flex flex-col flex-1 min-h-0 w-full max-w-2xl mx-auto px-4 overflow-hidden">
      {/* Secret background: rich animated gradient */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        aria-hidden
      >
        <div
          className="absolute inset-0 opacity-100 secret-mosaic-bg"
          style={{
            background: `
              radial-gradient(ellipse 120% 80% at 20% 20%, rgba(88, 28, 135, 0.85) 0%, transparent 50%),
              radial-gradient(ellipse 100% 100% at 80% 80%, rgba(30, 27, 75, 0.9) 0%, transparent 50%),
              radial-gradient(ellipse 80% 120% at 50% 50%, rgba(120, 53, 15, 0.4) 0%, transparent 45%),
              radial-gradient(ellipse 90% 90% at 70% 20%, rgba(49, 46, 129, 0.6) 0%, transparent 40%),
              linear-gradient(160deg, #0f0a1e 0%, #1a0a2e 30%, #0d0d1a 60%, #0a0f1e 100%)
            `,
          }}
        />
        <div
          className="absolute inset-0 secret-mosaic-shine"
          style={{
            background: `
              radial-gradient(circle at 30% 30%, rgba(216, 180, 254, 0.12) 0%, transparent 40%),
              radial-gradient(circle at 70% 70%, rgba(251, 191, 36, 0.08) 0%, transparent 40%)
            `,
          }}
        />
      </div>

      <style>{`
        @keyframes secret-mosaic-shift {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.95; transform: scale(1.02); }
        }
        @keyframes secret-mosaic-shine-move {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        .secret-mosaic-bg {
          animation: secret-mosaic-shift 8s ease-in-out infinite;
        }
        .secret-mosaic-shine {
          animation: secret-mosaic-shine-move 6s ease-in-out infinite;
        }
      `}</style>

      <AnimatePresence>
        {floatingCrystals != null && (
          <FloatingCrystals
            amount={floatingCrystals}
            onComplete={() => setFloatingCrystals(null)}
          />
        )}
      </AnimatePresence>

      <div className="relative z-10 flex flex-col flex-1 min-h-0">
        {headerRow}

        {/* Target term */}
        <motion.div
          className="flex flex-col items-center justify-center mb-6 py-6 px-4 rounded-2xl border border-white/20 bg-white/5 backdrop-blur-md shadow-xl min-h-[100px]"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 28 }}
        >
          <span className="flex items-center gap-2 text-sm font-medium text-white/70 uppercase tracking-wider mb-2">
            <Target className="w-4 h-4" aria-hidden />
            {t("find_translation_hint", "FIND THE TRANSLATION")}
          </span>
          {displayTargetCard ? (
            <motion.span
              key={displayTargetCard.id ?? displayTargetCard.front}
              className="text-2xl sm:text-3xl font-bold text-white text-center drop-shadow-lg"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
            >
              {(displayTargetCard.front || "").trim()}
            </motion.span>
          ) : (
            <span className="text-xl text-white/80">{t("secretMosaic.complete")}</span>
          )}
        </motion.div>

        {/* Grid of glass tiles */}
        <motion.div
          className="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4 w-full flex-1 min-h-0 content-start pb-10"
          layout
        >
          <AnimatePresence mode="popLayout">
            {roundCards.map((card, index) => {
              const isCleared = clearedSet.has(index);
              const isShaking = shakeIndex === index;

              if (isCleared) {
                return (
                  <motion.div
                    key={`${card.id ?? index}-cleared`}
                    layout
                    initial={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{
                      layout: { type: "spring", stiffness: 400, damping: 35 },
                      exit: { duration: 0.35, ease: "easeIn" },
                    }}
                    className="relative aspect-[4/3] min-h-[80px] rounded-xl"
                    style={{ pointerEvents: "none" }}
                  />
                );
              }

              return (
                <motion.div
                  key={`${card.id ?? index}`}
                  layout
                  initial={{ opacity: 1 }}
                  transition={{
                    layout: { type: "spring", stiffness: 400, damping: 35 },
                  }}
                  className="relative aspect-[4/3] min-h-[80px]"
                >
                  <motion.button
                    type="button"
                    layout
                    onClick={() => handleTileClick(index)}
                    className="relative w-full h-full rounded-xl overflow-hidden border-2 border-white/30 bg-white/10 backdrop-blur-md shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent transition-colors hover:border-white/50 hover:bg-white/15 hover:shadow-xl"
                    style={{
                      boxShadow: isShaking
                        ? "0 0 20px rgba(239, 68, 68, 0.5), inset 0 0 0 1px rgba(239, 68, 68, 0.3)"
                        : undefined,
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    animate={
                      isShaking
                        ? {
                            x: [0, -8, 8, -8, 8, 0],
                            transition: { duration: 0.4 },
                          }
                        : undefined
                    }
                  >
                    <span className="absolute inset-0 flex items-center justify-center p-3 text-sm font-medium text-white drop-shadow-md text-center break-words">
                      {(card.back || "").trim()}
                    </span>
                  </motion.button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
